import { isMagicFurnishStaffEmail } from './visitorSegregation';
import { normalizeEmail } from '../../utils/sessionAudience';
import { inferActorRole, METRIC_SCOPES, rowEventCategory } from '../../utils/eventAttribution';

/** Stable row key for deduping inquiry events across feed + timeseries. */
export const inquiryActivityRowId = (row) =>
  row?._id ||
  `${row?.timestamp}-${row?.detail?.inquirerEmail || row?.meta?.user}-${row?.meta?.event}`;

/** Dedupe by id and sort newest first (drill-down list + KPI totals). */
export const dedupeInquiryActivityRows = (rows) => {
  const byId = new Map();
  (rows || []).forEach((r) => {
    const id = inquiryActivityRowId(r);
    if (!byId.has(id)) byId.set(id, r);
  });
  return [...byId.values()].sort(
    (a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
  );
};

const inquirerEmailFromRow = (row) => normalizeEmail(row?.detail?.inquirerEmail);

/** Demand-scope inquiries — optional MF internal and partner-agent filters. */
export const inquiryMatchesDemandScope = (row, { excludeInternal = false, excludeAgentActivity = false } = {}) => {
  const category = rowEventCategory(row);
  if (category && category !== METRIC_SCOPES.demand) return false;

  const role = inferActorRole(row?.meta, row?.detail);
  if (role === 'mf_staff') return false;
  if (excludeAgentActivity && role === 'agent') return false;

  const hint = row?.detail?.audience;
  if (hint === 'internal') return false;
  if (excludeAgentActivity && hint === 'agent') return false;

  if (excludeInternal && inquiryExcludedAsInternal(row, true)) return false;
  return true;
};

export const inquiryExcludedAsInternal = (row, excludeInternal) => {
  if (!excludeInternal) return false;
  if (row?.detail?.audience === 'internal') return true;
  const email = inquirerEmailFromRow(row);
  return email ? isMagicFurnishStaffEmail(email) : false;
};

/**
 * Full-window inquiry_submitted rows after demand-scope + internal filters.
 */
export const buildFilteredInquiryRows = (
  rawRows,
  { excludeInternal = false, excludeAgentActivity = false } = {}
) =>
  dedupeInquiryActivityRows(rawRows || []).filter((row) =>
    inquiryMatchesDemandScope(row, { excludeInternal, excludeAgentActivity })
  );
